import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { ArrowLeftSquareFill, ArrowRightSquareFill, ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { useLocation } from 'react-router-dom';
import { MessageReply, MessageReplyProps } from '.';
import { Message, MessageFile, MessageThread } from '../api';
import { useBinaryObjectUrl } from '../hooks';
import { HcpModal } from './HcpModal';
import { INCOMING_MESSAGE, OUTGOING_MESSAGE, REPLY } from '../utils';
import { useSelector } from 'react-redux';
import { AppRootState } from '../redux';

interface MessageStepProps {
  canReply?: boolean;
  isMessageOpen?: boolean;
  replyProps: Partial<MessageReplyProps>;
  thread: MessageThread | null;
  threadMessage: Message | null;
}

export const MessageStep = (props: MessageStepProps) => {
  const { hash } = useLocation();
  const openByDefault = hash === '#reply';
  const [isMessageShown, setShowMessage] = useState(props.isMessageOpen);
  const [isReplyShown, setIsReplyShown] = useState(openByDefault);
  const files: MessageFile[] | null | undefined = props.threadMessage?.files;
  const isJanssen = String(useSelector((state: AppRootState) => state.auth.session?.user?.ProgramId ?? '')) == '127';

  const onShowClick = useCallback(() => setShowMessage((shown) => !shown), [setShowMessage]);
  const onReplyToggle = useCallback(() => setIsReplyShown((shown) => !shown), [setIsReplyShown]);

  const CaretIcon = isMessageShown ? ChevronDown : ChevronRight;
  let DirectionIcon;
  let directionIconColorClass;
  const dividerColorClass = '#009B92';
  let to;
  let from;
  let tooltip;

  if (props.threadMessage?.isSentFromCurrentUser) {
    DirectionIcon = ArrowLeftSquareFill;
    directionIconColorClass = 'gray';
    // dividerColorClass = 'text-gray-dark';
    if (!isJanssen) {
      to = props.thread?.assignedTo;
    }
    from = props.threadMessage?.sender;
    tooltip = OUTGOING_MESSAGE;
  } else {
    DirectionIcon = ArrowRightSquareFill;
    directionIconColorClass = 'gray';
    // dividerColorClass = 'border-primary';
    if (!isJanssen) {
      from = props.thread?.assignedTo;
    }
    to = props.threadMessage?.sender;
    tooltip = INCOMING_MESSAGE;
  }

  return (
    <Row className='mb-5'>
      <Col xs='auto'>
        {isMessageShown && (
          <i className='fa-regular fa-chevron-down' style={{ fontSize: '24px' }} onClick={onShowClick}></i>
        )}
        {!isMessageShown && (
          <i className='fa-regular fa-chevron-up' style={{ fontSize: '24px' }} onClick={onShowClick}></i>
        )}
      </Col>
      <Col xs='auto' className='justify-content-center'>
        <Row className='mb-1'>
          <Col className='text-center'>
            <OverlayTrigger placement='top' overlay={<Tooltip id='message-tooltip'>{tooltip}</Tooltip>}>
              <DirectionIcon style={{ color: 'gray' }} size={24} />
            </OverlayTrigger>
          </Col>
        </Row>
        <Row className='h-100'>
          <Col className='border-end border-secondary-new' />
          <Col />
        </Row>
      </Col>
      <Col data-testid='messageStepHeader' className='text-left col-10'>
        <Row data-testid='messageStepClickToShow' onClick={onShowClick} className='pointer'>
          <Col className='text-secondary'>
            {to && (
              <span className='mr-4'>
                From: <strong>{to} </strong>
              </span>
            )}
            {from && (
              <span>
                From: <strong>{from} </strong>
              </span>
            )}
          </Col>
          <Col className='text-right text-muted d-flex justify-content-end'>
            <MessageFiles files={files} />
            <small className='ml-3' style={{ color: '#212529' }}>
              {moment(props.threadMessage?.messageDate).format('h:mm a | MM/DD/YYYY')}
            </small>
          </Col>
        </Row>
        {isMessageShown && (
          <Row data-testid='messageDisplayContainer' className='mt-3'>
            <Col>
              <p className='section rounded border bg-white text-white-space-wrap'>
                {props.threadMessage?.messageText}
              </p>
            </Col>
            {props.canReply && (
              <Col xs='auto'>
                <OverlayTrigger placement='top' overlay={<Tooltip id='reply-message'>{REPLY}</Tooltip>}>
                  <Button className='py-2 px-3' onClick={onReplyToggle} variant='secondary-new' size='sm'>
                    <i className='fa-sharp fa-solid fa-reply'></i>
                  </Button>
                </OverlayTrigger>
              </Col>
            )}
          </Row>
        )}
        {props.canReply && isReplyShown && (
          <MessageReply
            {...props.replyProps}
            onCancel={onReplyToggle}
            thread={props.thread ?? null}
            threadMessage={props.threadMessage ?? null}
          />
        )}
      </Col>
    </Row>
  );
};

function MessageFiles({ files }: { files: MessageFile[] | null | undefined }) {
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<MessageFile | undefined>(undefined);
  const selectedFileUrl = useBinaryObjectUrl(selectedFile?.fileData, 'application/pdf');

  const onFileClick = (file: MessageFile) => {
    setSelectedFile(file);
    setModalShow(true);
  };

  return (
    <>
      {files?.map((file, i, arr) => (
        <small key={file.documentId}>
          <span
            className='text-secondary'
            onClick={(e) => {
              e.stopPropagation();
              onFileClick(file);
            }}>
            <span role='link'>{file.fileName}</span>
            {arr.length - 1 !== i ? ', ' : ''}
          </span>
        </small>
      ))}
      <HcpModal
        enableCancel
        modalProps={{ show: modalShow, onHide: () => setModalShow(false) }}
        header={selectedFile?.fileName ?? 'No file name'}
        body={
          <object
            aria-label={`Document ${selectedFile?.fileName}`}
            height={600}
            width='100%'
            type='application/pdf'
            name={selectedFile?.fileName?.toString()}
            data={selectedFileUrl}
          />
        }
      />
    </>
  );
}
